import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const workshopApi = createApi({
    reducerPath: 'workshopApi',
    baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
    tagTypes: [
        'Workshops',
        'IsThisYou',
        'WhyJoinThis',
        'WhatYouLearn',
        'WhoIsThisFor',
        'WorkshopFAQ',
    ],
    endpoints: builder => ({
        getAllWorkshops: builder.query({
            query: () => `/workshops/all`,
            providesTags: ['Workshops'],
        }),

        getWorkshopBySlug: builder.query({
            query: slug => `/workshops/${slug}/`,
            providesTags: ['Workshops'],
        }),

        deleteWorkshop: builder.mutation({
            query: slug => ({
                url: `/workshops/delete/${slug}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Workshops'],
        }),

        updateWorkshop: builder.mutation({
            query: ({ slug, formData }) => ({
                url: `/workshops/partial/${slug}/`,
                method: 'PATCH',
                body: formData,
                formData: true,
            }),
            invalidatesTags: ['Workshops'],
        }),

        createWorkshop: builder.mutation({
            query: formData => ({
                url: `/workshops/create`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Workshops'],
        }),

        // Is This You Section
        getIsThisYouBySlug: builder.query({
            query: slug => `/workshops/is-this-you/${slug}/`,
            providesTags: (result, error, slug) => [{ type: 'IsThisYou', id: slug }],
        }),

        updateIsThisYou: builder.mutation({
            query: ({ slug, formData }) => ({
                url: `/workshops/is-this-you/update/${slug}/`,
                method: 'PATCH',
                body: formData,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'IsThisYou', id: slug }],
        }),

        createIsThisYou: builder.mutation({
            query: ({ slug, formData }) => ({
                url: `/workshops/is-this-you/create/${slug}/`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Workshops'],
        }),

        // Why join This  Section
        getWhyJoinThisBySlug: builder.query({
            query: slug => `/workshops/why-join/${slug}/`,
            providesTags: (result, error, slug) => [{ type: 'WhyJoinThis', id: slug }],
        }),

        updateWhyJoinThis: builder.mutation({
            query: ({ slug, topicSlug, body }) => ({
                url: `/workshops/why-join/update/${slug}/${topicSlug}/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'WhyJoinThis', id: slug }],
        }),

        deleteWhyJoinThis: builder.mutation({
            query: ({ slug, topicSlug }) => ({
                url: `/workshops/why-join/delete/${slug}/${topicSlug}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'WhyJoinThis', id: slug }],
        }),

        createWhyJoinThis: builder.mutation({
            query: ({ slug, formData }) => ({
                url: `/workshops/why-join/create/${slug}/`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Workshops'],
        }),

        // What You Will Learn Section

        getWhatYouLearnBySlug: builder.query({
            query: slug => `/workshops/what-youll-learn/${slug}/`,
            providesTags: (result, error, slug) => [{ type: 'WhyJoinThis', id: slug }],
        }),

        updateWhatYouLearn: builder.mutation({
            query: ({ slug, topicSlug, body }) => ({
                url: `/workshops/what-youll-learn/update/${slug}/${topicSlug}/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'WhyJoinThis', id: slug }],
        }),

        deleteWhatYouLearn: builder.mutation({
            query: ({ slug, topicSlug }) => ({
                url: `/workshops/what-youll-learn/delete/${slug}/${topicSlug}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'WhyJoinThis', id: slug }],
        }),

        createWhatYouLearn: builder.mutation({
            query: ({ slug, formData }) => ({
                url: `/workshops/what-youll-learn/create/${slug}/`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Workshops'],
        }),

        // Who is this for Section

        getWhoIsThisForBySlug: builder.query({
            query: slug => `/workshops/who-is-this-for/${slug}/`,
            providesTags: (result, error, slug) => [{ type: 'WhoIsThisFor', id: slug }],
        }),

        updateWhoIsThisForLearn: builder.mutation({
            query: ({ slug, topicSlug, body }) => ({
                url: `/workshops/who-is-this-for/update/${slug}/${topicSlug}/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'WhoIsThisFor', id: slug }],
        }),

        deleteWhoIsThisFor: builder.mutation({
            query: ({ slug, topicSlug }) => ({
                url: `/workshops/who-is-this-for/delete/${slug}/${topicSlug}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { slug }) => [{ type: 'WhoIsThisFor', id: slug }],
        }),

        createWhoIsThisFor: builder.mutation({
            query: ({ slug, formData }) => ({
                url: `/workshops/who-is-this-for/create/${slug}/`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['Workshops'],
        }),

        // workshop faq Section

        getFaqBySlug: builder.query({
            query: slug => `/faq/${slug}/`,
            providesTags: (result, error, slug) => [{ type: 'WorkshopFAQ', id: slug }],
        }),

        addFaq: builder.mutation({
            query: ({ workshopSlug, question, answer }) => ({
                url: `faq/${workshopSlug}/add/`,
                method: 'POST',
                body: { question, answer },
            }),
            invalidatesTags: ['WorkshopFAQ'],
        }),

        deleteFaq: builder.mutation({
            query: ({ workshopSlug, qaId }) => ({
                url: `faq/${workshopSlug}/delete/${qaId}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['WorkshopFAQ'],
        }),
    }),
});

export const {
    useGetAllWorkshopsQuery,
    useGetWorkshopBySlugQuery,
    useDeleteWorkshopMutation,
    useUpdateWorkshopMutation,
    useCreateWorkshopMutation,

    // Is This You Section
    useGetIsThisYouBySlugQuery,
    useUpdateIsThisYouMutation,
    useCreateIsThisYouMutation,

    // Why join This  Section
    useGetWhyJoinThisBySlugQuery,
    useUpdateWhyJoinThisMutation,
    useDeleteWhyJoinThisMutation,
    useCreateWhyJoinThisMutation,

    // What You Will Learn Section
    useGetWhatYouLearnBySlugQuery,
    useUpdateWhatYouLearnMutation,
    useDeleteWhatYouLearnMutation,
    useCreateWhatYouLearnMutation,

    // Who is this for Section
    useGetWhoIsThisForBySlugQuery,
    useUpdateWhoIsThisForLearnMutation,
    useDeleteWhoIsThisForMutation,
    useCreateWhoIsThisForMutation,

    // workshop faq Section
    useGetFaqBySlugQuery,
    useAddFaqMutation,
    useDeleteFaqMutation,
} = workshopApi;
